import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Tabs } from '@opendate-app/setlist';
import StructureTab from './StructureTab';
import AppearanceTab from './AppearanceTab';
import EmbedTab from './EmbedTab';
import PreviewFrame from './PreviewFrame';
import EventOrderForm from '../EventOrderForm';
import OrderFormPreview from '../../../TicketingSettings/components/OrderFormPreview';
import { toastError } from '../../../../shared/toastHelper';
import './customWidgetBuilder.css';
const axios = require('axios').default;

const RIGHT_TABS = [
  { value: 'structure',  label: 'Structure' },
  { value: 'appearance', label: 'Appearance' },
  { value: 'fields',     label: 'Fields' },
  { value: 'embed',      label: 'Embed' }
];

const CustomWidgetBuilder = ({
  csrfToken,
  confirm,
  team,
  widgetConfig,
  widgetTemplates: widgetTemplatesProp,
  checkoutCategories,
  ticketTypes,
  addOns,
  memberships,
  seatingChartEvents,
  webOrderFormProps,
  onWidgetConfigChange,
  onCheckoutCategoriesChange,
  onWidgetTemplatesChange,
  confirmSelfManaging,
  reloadCheckoutCategories,
  selfManagedAlert,
  checkoutStructureSelfManaged,
  orderFormAvailable
}) => {
  const [activeTab, setActiveTab] = useState('structure');
  const [deviceMode, setDeviceMode] = useState('desktop');
  const [orderFormFields, setOrderFormFields] = useState([]);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [widgetTemplates, setWidgetTemplates] = useState(widgetTemplatesProp || []);

  const togglePanel = () => setPanelCollapsed((prev) => !prev);

  useEffect(() => {
    axios.get(`/teams/${team.id}/widget_templates`)
      .then(({ data }) => {
        setWidgetTemplates(data);
        if (onWidgetTemplatesChange) onWidgetTemplatesChange(data);
      })
      .catch(() => { /* dropdown gracefully degrades to default-only */ });
  }, [team.id]);

  const applyTemplateId = (templateId) => {
    if (!widgetConfig.id) return;
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    return axios.patch(`/teams/${team.id}/widget_configs/${widgetConfig.id}`, {
      widget_config: { widget_template_id: templateId || null }
    })
      .then(({ data }) => onWidgetConfigChange(data))
      .catch(() => toastError('Failed to update template'));
  };

  return (
    <div className="custom-widget-builder d-flex" style={{ height: '100%', minHeight: 'calc(80vh - 60px)' }}>
      <div className="cwb-builder-preview flex-grow-1 d-flex flex-column align-items-stretch"
           style={{ padding: '25px', background: 'var(--sl-bg-secondary)' }}>
        {checkoutStructureSelfManaged && selfManagedAlert && (
          <div style={{ paddingBottom: '20px' }}>{selfManagedAlert}</div>
        )}
        {!panelCollapsed && (
          <button
            type="button"
            className="cwb-panel-collapse-btn"
            onClick={togglePanel}
            title="Collapse panel"
            aria-label="Collapse panel">
            <ChevronRight width={16} height={16} aria-hidden="true" />
          </button>
        )}
        <PreviewFrame
          confirmId={confirm.id}
          confirm={confirm}
          widgetConfig={widgetConfig}
          checkoutCategories={checkoutCategories}
          ticketTypes={ticketTypes}
          addOns={addOns}
          deviceMode={deviceMode}
          onDeviceModeChange={setDeviceMode}
          webOrderFormProps={webOrderFormProps}
          replacement={activeTab === 'fields' ? <OrderFormPreview fields={orderFormFields} ticketTypes={ticketTypes} subtitle="Sample order" /> : null}
        />
      </div>

      <div className={`cwb-builder-panel-wrap ${panelCollapsed ? 'collapsed' : ''}`}>
        <button
          type="button"
          className="cwb-panel-collapsed-strip"
          onClick={togglePanel}
          title="Expand panel"
          aria-label="Expand panel">
          <ChevronLeft width={16} height={16} aria-hidden="true" style={{ marginBottom: '8px' }} />
          <span className="cwb-panel-collapsed-label">Panel</span>
        </button>

        <div className="cwb-builder-panel">
          <div className="cwb-builder-tabs">
            <Tabs
              aria-label="Builder sections"
              variant="button-border"
              size="sm"
              fullWidth
              collapseBelow={0}
              items={orderFormAvailable ? RIGHT_TABS : RIGHT_TABS.filter((tab) => tab.value !== 'fields')}
              value={activeTab}
              onChange={setActiveTab}
            />
          </div>

          {activeTab === 'structure' && (
            <StructureTab
              csrfToken={csrfToken}
              team={team}
              confirm={confirm}
              widgetConfig={widgetConfig}
              widgetTemplates={widgetTemplates}
              checkoutCategories={checkoutCategories}
              ticketTypes={ticketTypes}
              addOns={addOns}
              memberships={memberships}
              seatingChartEvents={seatingChartEvents}
              onCheckoutCategoriesChange={onCheckoutCategoriesChange}
              onApplyTemplateId={applyTemplateId}
              confirmSelfManaging={confirmSelfManaging}
              reloadCheckoutCategories={reloadCheckoutCategories}
            />
          )}
          {activeTab === 'appearance' && (
            <div style={{ paddingTop: '4px' }}>
              <AppearanceTab
                csrfToken={csrfToken}
                team={team}
                widgetConfig={widgetConfig}
                widgetTemplates={widgetTemplates}
                onWidgetConfigChange={onWidgetConfigChange}
                onWidgetTemplatesChange={(updated) => {
                  setWidgetTemplates(updated);
                  if (onWidgetTemplatesChange) onWidgetTemplatesChange(updated);
                }}
              />
            </div>
          )}
          {activeTab === 'fields' && (
            <div style={{ paddingTop: '4px' }}>
              <EventOrderForm team={team} confirm={confirm} compact onFieldsChange={setOrderFormFields} ticketTypes={ticketTypes} />
            </div>
          )}
          {activeTab === 'embed' && (
            <div style={{ paddingTop: '4px' }}>
              <EmbedTab
                confirm={confirm}
                widgetConfig={widgetConfig}
                widgetTemplates={widgetTemplates}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomWidgetBuilder;
